import { t } from "@redotech/redo-i18n";

export type CatalogReadinessField =
  | "hsCode"
  | "customsDescription"
  | "originCountry"
  | "manufacturerId";

export type EuProductIdentifierField =
  | "merchantProductId"
  | "manufacturerProductId"
  | "standardizedProductId";

export type MerchantCustomsGapField =
  | CatalogReadinessField
  | EuProductIdentifierField;

export interface CatalogReadinessSummary {
  destinationRequiredFields: readonly CatalogReadinessField[];
  euProductIdentifiersRequired: boolean;
  catalog: {
    total: number;
    missingHsCode: number;
    missingCustomsDescription: number;
    missingOriginCountry: number;
    missingManufacturerId: number;
    missingMerchantProductId: number;
    missingManufacturerProductId: number;
    missingStandardizedProductId: number;
  };
}

export interface MerchantCustomsGap {
  field: MerchantCustomsGapField;
  label: string;
  count: number;
}

export interface MerchantCustomsGapOptions {
  /** Current form values, so an unsaved toggle previews its effect. */
  hsCodeAssisted: boolean;
  descriptionAssisted: boolean;
}

/** Merchant-filled fields: destination requirements minus Redo-assisted ones. */
export function merchantRequiredCustomsFields(
  destinationRequiredFields: readonly CatalogReadinessField[],
  options: MerchantCustomsGapOptions,
): CatalogReadinessField[] {
  if (destinationRequiredFields.length === 0) {
    return [];
  }
  const fields = destinationRequiredFields.filter(
    (field) => field !== "hsCode" || !options.hsCodeAssisted,
  );
  if (!options.descriptionAssisted) {
    fields.push("customsDescription");
  }
  return fields;
}

function gapLabel(field: MerchantCustomsGapField): string {
  switch (field) {
    case "originCountry":
      return t("Countries of origin");
    case "manufacturerId":
      return t("Manufacturer");
    case "hsCode":
      return t("HS codes");
    case "customsDescription":
      return t("Customs descriptions");
    case "merchantProductId":
      return t("SKUs");
    case "manufacturerProductId":
      return t("Manufacturer part numbers");
    case "standardizedProductId":
      return t("Barcodes (where the product has one)");
  }
}

const MISSING_COUNT: Record<
  MerchantCustomsGapField,
  (catalog: CatalogReadinessSummary["catalog"]) => number
> = {
  originCountry: (catalog) => catalog.missingOriginCountry,
  manufacturerId: (catalog) => catalog.missingManufacturerId,
  hsCode: (catalog) => catalog.missingHsCode,
  customsDescription: (catalog) => catalog.missingCustomsDescription,
  merchantProductId: (catalog) => catalog.missingMerchantProductId,
  manufacturerProductId: (catalog) => catalog.missingManufacturerProductId,
  standardizedProductId: (catalog) => catalog.missingStandardizedProductId,
};

/** Display order: merchant-only fields first, Redo-assisted fields last. */
const GAP_ORDER: readonly MerchantCustomsGapField[] = [
  "originCountry",
  "manufacturerId",
  "merchantProductId",
  "manufacturerProductId",
  "standardizedProductId",
  "hsCode",
  "customsDescription",
];

const EU_PRODUCT_IDENTIFIER_FIELDS: readonly EuProductIdentifierField[] = [
  "merchantProductId",
  "manufacturerProductId",
  "standardizedProductId",
];

/** Customs data only the merchant can supply, given their destinations, EU
 * markets, and Redo-assisted opt-outs. Complete fields are kept at zero. */
export function merchantCustomsGaps(
  readiness: CatalogReadinessSummary,
  options: MerchantCustomsGapOptions,
): MerchantCustomsGap[] {
  const required = new Set<MerchantCustomsGapField>([
    ...merchantRequiredCustomsFields(
      readiness.destinationRequiredFields,
      options,
    ),
    ...(readiness.euProductIdentifiersRequired
      ? EU_PRODUCT_IDENTIFIER_FIELDS
      : []),
  ]);
  return GAP_ORDER.filter((field) => required.has(field)).map((field) => ({
    field,
    label: gapLabel(field),
    count: MISSING_COUNT[field](readiness.catalog),
  }));
}
